/**
 * B-Q1 (constitution closure 2026-10-19) — the Dayboard ticket card's vehicle, in the shape the card reads.
 *
 * `TicketCard` renders `{year} {make} {model}` plus ` • <engine>` from `ticket.vehicle.engineSize` (camelCase, the
 * `Vehicle` type in src/types). The Dayboard (`src/pages/Dashboard.tsx`) selects the raw PostgREST row
 * `vehicle:vehicles(id, year, make, model, engine_size)` and spread it straight into the ticket, so `engineSize` was
 * always undefined there and the engine text never showed on the board, while the ticket popup (which reads both
 * spellings) did show it. The mapping belongs at the caller: TicketCard stays byte-identical (W5 single-bay parity).
 *
 * The raw row is kept as it is (every existing reader of `vehicle.engine_size` keeps working); only the card's
 * camelCase field is added. An empty / missing engine stays absent — nothing is invented.
 */
export interface DayboardRowVehicle {
  engine_size?: string | null;
  engineSize?: string | null;
  [key: string]: unknown;
}

export function dayboardCardVehicle<V extends DayboardRowVehicle>(
  vehicle: V | null | undefined,
  // OCT28 #1: plate + issuing state reach the card only for viewers of the vehicle-identity rule (canViewVehicleIdentity).
  opts: { showPlate?: boolean } = {},
): (V & { engineSize?: string; licensePlate?: string; plateJurisdiction?: string }) | null | undefined {
  if (!vehicle) return vehicle;
  const raw = vehicle.engineSize ?? vehicle.engine_size;
  const engineSize = typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
  if (!opts.showPlate) return { ...vehicle, engineSize };
  const plate = typeof vehicle.license_plate === 'string' && vehicle.license_plate.trim() !== '' ? vehicle.license_plate : undefined;
  const jur = typeof vehicle.plate_jurisdiction === 'string' && vehicle.plate_jurisdiction.trim() !== '' ? vehicle.plate_jurisdiction : undefined;
  return { ...vehicle, engineSize, licensePlate: plate, plateJurisdiction: jur };
}
